/**
 * @file Frontend utilities — contributorFormFieldInputProps.ts.
 */

import type { InputHTMLAttributes } from "react";
import type { FormField } from "../interfaces/formInterfaces";

function parseOptionalNumber(
  v: string | number | null | undefined
): number | undefined {
  if (v === null || v === undefined) return undefined;
  const s = String(v).trim();
  if (s === "") return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

function decimalStep(maxDecimalPlaces: number | null | undefined): number | "any" {
  const dp =
    maxDecimalPlaces != null ? Number(maxDecimalPlaces) : Number.NaN;
  if (!Number.isFinite(dp) || dp < 0) return "any";
  if (dp === 0) return 1;
  return 10 ** -dp;
}

/** API may send textMaxLength as string; treat empty string as unset. */
function parseTextMaxLength(field: FormField): number | undefined {
  const raw = field.textMaxLength as unknown;
  if (raw === null || raw === undefined) return undefined;
  const s = String(raw).trim();
  if (s === "") return undefined;
  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function applyTextMaxLengthForTextualType(
  props: InputHTMLAttributes<HTMLInputElement>,
  field: FormField
): void {
  const ty = String(props.type ?? "text").toLowerCase();
  const textual =
    ty === "text" ||
    ty === "email" ||
    ty === "search" ||
    ty === "url" ||
    ty === "tel" ||
    ty === "password";
  if (!textual) return;
  const ml = parseTextMaxLength(field);
  if (ml !== undefined) {
    props.maxLength = ml;
  }
}

function resolveContributorTextInputHtmlProps(
  field: FormField
): Pick<
  InputHTMLAttributes<HTMLInputElement>,
  "type" | "min" | "max" | "maxLength" | "step"
> {
  if (field.type === "EMAIL") {
    return { type: "email" };
  }

  if (field.type !== "TEXT") {
    const t = String(field.inputType ?? "")
      .trim()
      .toLowerCase();
    if (t === "number" || t === "integer") {
      const props: InputHTMLAttributes<HTMLInputElement> = {
        type: "number",
        step: 1,
      };
      const min = parseOptionalNumber(field.numberMin);
      const max = parseOptionalNumber(field.numberMax);
      if (min !== undefined) props.min = min;
      if (max !== undefined) props.max = max;
      return props;
    }
    if (t === "double") {
      const props: InputHTMLAttributes<HTMLInputElement> = {
        type: "number",
        step: decimalStep(field.maxDecimalPlaces),
      };
      const min = parseOptionalNumber(field.numberMin);
      const max = parseOptionalNumber(field.numberMax);
      if (min !== undefined) props.min = min;
      if (max !== undefined) props.max = max;
      return props;
    }
    return { type: (field.inputType as string) || "text" };
  }

  const it = String(field.inputType ?? "")
    .trim()
    .toLowerCase();

  if (it === "number" || it === "integer") {
    const props: InputHTMLAttributes<HTMLInputElement> = {
      type: "number",
      step: 1,
    };
    const min = parseOptionalNumber(field.numberMin);
    const max = parseOptionalNumber(field.numberMax);
    if (min !== undefined) props.min = min;
    if (max !== undefined) props.max = max;
    return props;
  }

  if (it === "double") {
    const props: InputHTMLAttributes<HTMLInputElement> = {
      type: "number",
      step: decimalStep(field.maxDecimalPlaces),
    };
    const min = parseOptionalNumber(field.numberMin);
    const max = parseOptionalNumber(field.numberMax);
    if (min !== undefined) props.min = min;
    if (max !== undefined) props.max = max;
    return props;
  }

  return {
    type: "text",
  };
}

/**
 * Maps CMS `FormField` validation (inputType, textMaxLength, numberMin/Max, maxDecimalPlaces)
 * to native input attributes for contributor text-like inputs.
 */
export function getContributorTextInputHtmlProps(
  field: FormField
): Pick<
  InputHTMLAttributes<HTMLInputElement>,
  "type" | "min" | "max" | "maxLength" | "step"
> {
  const props = resolveContributorTextInputHtmlProps(field);
  applyTextMaxLengthForTextualType(props, field);
  return props;
}

/**
 * For legacy numeric fields (e.g. name-based heuristics) read min/max/step from CMS when inputType is number/double.
 */
export function getContributorNumericHtmlPropsFromCms(
  field: FormField
): Pick<
  InputHTMLAttributes<HTMLInputElement>,
  "min" | "max" | "step"
> {
  const it = String(field.inputType ?? "")
    .trim()
    .toLowerCase();
  if (it === "number" || it === "integer") {
    const out: InputHTMLAttributes<HTMLInputElement> = { step: 1 };
    const min = parseOptionalNumber(field.numberMin);
    const max = parseOptionalNumber(field.numberMax);
    if (min !== undefined) out.min = min;
    if (max !== undefined) out.max = max;
    return out;
  }
  if (it === "double") {
    const out: InputHTMLAttributes<HTMLInputElement> = {
      step: decimalStep(field.maxDecimalPlaces),
    };
    const min = parseOptionalNumber(field.numberMin);
    const max = parseOptionalNumber(field.numberMax);
    if (min !== undefined) out.min = min;
    if (max !== undefined) out.max = max;
    return out;
  }
  return {};
}
